/**
 * columnResizeMixin: shared column drag-resize behavior for tables.
 * Snapshots widths, clamps resize to the real table container, and on expand
 * shrinks columns to the right so the last columns stay visible (min 60px).
 * Used by mapping pages (edit-control, system/project control mapping) and mirrored in tableData/newTable.
 */
export const columnResizeMixin = {
	data() {
		return {
			columnWidths: {} as Record<string, number>,
			resizingColumn: null as any,
			resizeStartX: 0,
			resizeStartWidth: 0,
			didResize: false,
		};
	},
	computed: {
		tableResizeStyle(): any {
			const self = this as any;
			const names = Array.from(
				(typeof self.getResizeColumnNames === 'function'
					? self.getResizeColumnNames()
					: self.columnNames) || []
			) as string[];
			const widths = self.columnWidths || {};
			const measured = names.filter((name: string) => Number(widths[name]) > 0);
			if (!names.length || measured.length !== names.length) {
				return {
					tableLayout: 'fixed',
					width: '100%',
					maxWidth: '100%',
				};
			}
			const containerWidth = self.getContainerWidth();
			const total = names.reduce((sum: number, name: string) => sum + Number(widths[name]), 0);
			const width = Math.min(total, containerWidth);
			return {
				tableLayout: 'fixed',
				width: width + 'px',
				minWidth: width + 'px',
				maxWidth: '100%',
			};
		},
	},
	beforeUnmount() {
		(this as any).stopColumnResize();
	},
	methods: {
		getResizeColumnNames(): string[] {
			return [];
		},
		columnStyle(column: any) {
			const width = (this as any).columnWidths[column];
			if (!width) {
				return {};
			}
			return {
				width: width + 'px',
				minWidth: width + 'px',
				maxWidth: width + 'px',
			};
		},
		snapshotColumnWidths() {
			const self = this as any;
			const headers = self.$el?.querySelectorAll?.('.table-header-table thead th') || [];
			const names = Array.from(self.getResizeColumnNames() || []) as string[];
			const next: Record<string, number> = { ...self.columnWidths };
			headers.forEach((th: HTMLElement, index: number) => {
				const column = names[index];
				if (column && next[column] == null) {
					next[column] = Math.max(60, Math.round(th.getBoundingClientRect().width));
				}
			});
			self.columnWidths = next;
		},
		getContainerWidth() {
			const root = (this as any).$el as HTMLElement | null;
			const container =
				(root?.querySelector?.('.table-container-fit') as HTMLElement | null) ||
				(root?.querySelector?.('.table-container') as HTMLElement | null) ||
				(root?.querySelector?.('.table-header-wrap') as HTMLElement | null);
			if (container) {
				return Math.max(60, Math.floor(container.clientWidth || container.getBoundingClientRect().width));
			}
			return Math.max(60, Math.floor(root?.clientWidth || 800));
		},
		maxWidthForColumn(column: string, proposedWidth: number) {
			const self = this as any;
			const names = Array.from(self.getResizeColumnNames() || []) as string[];
			const otherWidth = names.reduce((sum: number, name: string) => {
				if (name === column) {
					return sum;
				}
				return sum + (Number(self.columnWidths[name]) || 60);
			}, 0);
			const containerWidth = self.getContainerWidth();
			const maxTotal = Math.max(60, containerWidth - otherWidth);
			return Math.min(proposedWidth, maxTotal, containerWidth);
		},
		clampWidthsToContainer() {
			const self = this as any;
			const names = Array.from(self.getResizeColumnNames() || []) as string[];
			if (!names.length || !Object.keys(self.columnWidths || {}).length) {
				return;
			}
			const containerWidth = self.getContainerWidth();
			const total = names.reduce((sum: number, name: string) => sum + (Number(self.columnWidths[name]) || 0), 0);
			if (total <= containerWidth) {
				return;
			}
			const scale = containerWidth / total;
			const next: Record<string, number> = { ...self.columnWidths };
			names.forEach((name: string) => {
				if (next[name]) {
					next[name] = Math.max(60, Math.round(next[name] * scale));
				}
			});
			let overflow = names.reduce((sum: number, name: string) => sum + (Number(next[name]) || 0), 0) - containerWidth;
			for (let i = names.length - 1; i >= 0 && overflow > 0; i--) {
				const name = names[i];
				const current = Number(next[name]) || 0;
				if (current > 60) {
					const reduced = Math.max(60, current - overflow);
					overflow -= current - reduced;
					next[name] = reduced;
				}
			}
			self.columnWidths = next;
		},
		/** When expanding a column, shrink columns to its right so the total stays within the container. */
		applyResizeKeepingRightVisible(column: string, proposedWidth: number) {
			const self = this as any;
			const names = Array.from(self.getResizeColumnNames() || []) as string[];
			const idx = names.indexOf(column);
			if (idx < 0) {
				return { [column]: self.maxWidthForColumn(column, proposedWidth) };
			}
			const containerWidth = self.getContainerWidth();
			const leftWidth = names.slice(0, idx).reduce((sum: number, name: string) => {
				return sum + (Number(self.columnWidths[name]) || 60);
			}, 0);
			const rightNames = names.slice(idx + 1);
			const minRight = rightNames.length * 60;
			const maxForResized = Math.max(60, containerWidth - leftWidth - minRight);
			const nextWidth = Math.min(Math.max(60, proposedWidth), maxForResized, containerWidth);
			const next: Record<string, number> = { ...self.columnWidths, [column]: nextWidth };
			const usedLeftAndResized = leftWidth + nextWidth;
			let remaining = Math.max(0, containerWidth - usedLeftAndResized);
			const currentRightTotal = rightNames.reduce((sum: number, name: string) => {
				return sum + (Number(self.columnWidths[name]) || 60);
			}, 0);
			if (rightNames.length && currentRightTotal > remaining) {
				const scale = remaining / currentRightTotal;
				rightNames.forEach((name: string) => {
					next[name] = Math.max(60, Math.round((Number(self.columnWidths[name]) || 60) * scale));
				});
				let overflow =
					rightNames.reduce((sum: number, name: string) => sum + (Number(next[name]) || 0), 0) - remaining;
				for (let i = rightNames.length - 1; i >= 0 && overflow > 0; i--) {
					const name = rightNames[i];
					const current = Number(next[name]) || 0;
					if (current > 60) {
						const reduced = Math.max(60, current - overflow);
						overflow -= current - reduced;
						next[name] = reduced;
					}
				}
			}
			return next;
		},
		startColumnResize(event: MouseEvent, column: any) {
			const self = this as any;
			self.snapshotColumnWidths();
			self.clampWidthsToContainer();
			const th = (event.currentTarget as HTMLElement)?.closest('th');
			self.resizingColumn = column;
			self.resizeStartX = event.clientX;
			self.resizeStartWidth = self.columnWidths[column] || th?.getBoundingClientRect().width || 60;
			self.didResize = false;
			document.body.classList.add('col-resizing');
			document.addEventListener('mousemove', self.onColumnResize);
			document.addEventListener('mouseup', self.stopColumnResize);
		},
		onColumnResize(event: MouseEvent) {
			const self = this as any;
			if (!self.resizingColumn) {
				return;
			}
			const delta = event.clientX - self.resizeStartX;
			if (Math.abs(delta) > 2) {
				self.didResize = true;
			}
			const rawWidth = Math.max(60, Math.round(self.resizeStartWidth + delta));
			if (delta > 0) {
				self.columnWidths = self.applyResizeKeepingRightVisible(self.resizingColumn, rawWidth);
			} else {
				self.columnWidths = {
					...self.columnWidths,
					[self.resizingColumn]: self.maxWidthForColumn(self.resizingColumn, rawWidth),
				};
			}
		},
		stopColumnResize() {
			const self = this as any;
			self.resizingColumn = null;
			document.body.classList.remove('col-resizing');
			document.removeEventListener('mousemove', self.onColumnResize);
			document.removeEventListener('mouseup', self.stopColumnResize);
			self.clampWidthsToContainer();
		},
		onHeaderClick(column: any) {
			const self = this as any;
			if (self.didResize) {
				self.didResize = false;
				return;
			}
			if (typeof self.sort === 'function') {
				self.sort(column);
			}
		},
	},
};
